import React, { useCallback, useEffect, useState } from "react";

import {
	EstadoAvisos, activarAvisos, alCambiarInstalable, esAndroid, esIOS, esSafariIOS, estadoAvisos, instalada, instalar,
	prepararAvisos, probarAviso, puedeInstalarConBoton,
} from "./appMovil";
import "./appMovil.css";

/** El ícono "Compartir" de Safari (cuadro con flecha hacia arriba), para que lo reconozcan en su pantalla. */
export const IconoCompartir: React.FC = () => (
	<svg className="am-ico" viewBox="0 0 24 24" aria-hidden="true">
		<path d="M12 3v12M8 7l4-4 4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M8 10H6.5A1.5 1.5 0 0 0 5 11.5v8A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-8a1.5 1.5 0 0 0-1.5-1.5H16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
	</svg>
);

const IconoAgregar: React.FC = () => (
	<svg className="am-ico" viewBox="0 0 24 24" aria-hidden="true">
		<rect x="4" y="4" width="16" height="16" rx="4" fill="none" stroke="currentColor" strokeWidth="1.8" />
		<path d="M12 8.5v7M8.5 12h7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
	</svg>
);

/**
 * "WAPP en tu Celular" (ruta /app y botón en el menú de la persona): instalar WAPP y activar los avisos.
 * Esteban, 2026-10-09: "standalone web app para que en celular android y apple pueda instalar WappChat, WappTeam, CRM y
 * el resto de módulos… para que les lleguen notificaciones a las chicas".
 */
const AppMovilView: React.FC = () => {
	const [yaInstalada] = useState<boolean>(instalada);
	const [conBoton, setConBoton] = useState<boolean>(puedeInstalarConBoton);
	const [avisos, setAvisos] = useState<EstadoAvisos | null>(null);
	const [ocupado, setOcupado] = useState<boolean>(false);
	const [nota, setNota] = useState<string>("");
	const [error, setError] = useState<string>("");

	const refrescar = useCallback(() => { estadoAvisos().then(setAvisos).catch(() => setAvisos("no-soportado")); }, []);
	useEffect(() => { prepararAvisos(); refrescar(); }, [refrescar]);
	useEffect(() => alCambiarInstalable(() => setConBoton(puedeInstalarConBoton())), []);
	useEffect(() => {
		const f = () => { if (document.visibilityState === "visible") refrescar(); };
		document.addEventListener("visibilitychange", f);
		return () => document.removeEventListener("visibilitychange", f);
	}, [refrescar]);

	const activar = async () => {
		setOcupado(true); setError(""); setNota("");
		try {
			setAvisos(await activarAvisos());
		} catch (e: any) {
			setError(e?.response?.data?.message || e?.message || "No se pudieron activar los avisos.");
		} finally {
			setOcupado(false);
		}
	};

	const probar = async () => {
		setOcupado(true); setError(""); setNota("");
		try {
			const r = await probarAviso();
			setNota(r.entregados ? "Listo. Te debe llegar un aviso en unos segundos." : "No se pudo entregar el aviso. Toca Activar Avisos otra vez.");
			if (!r.entregados) setAvisos("inactivos");
		} catch (e: any) {
			setError(e?.response?.data?.message || "No se pudo mandar el aviso de prueba.");
		} finally {
			setOcupado(false);
		}
	};

	const ios = esIOS();
	const android = esAndroid();

	return (
		<div className="am">
			<div className="am-cabeza">
				<img src="/icons/wapp-192.png" alt="" className="am-logo" />
				<h1>WAPP en tu Celular</h1>
			</div>

			<section className="am-caja">
				<h2>Instalar</h2>
				{yaInstalada ? (
					<p className="am-ok">WAPP ya está instalada en este aparato.</p>
				) : ios && !esSafariIOS() ? (
					<p>Abre <b>wapp.washingtonacademy.com</b> en <b>Safari</b> para instalarla.</p>
				) : ios ? (
					<ol className="am-pasos">
						<li>Toca <IconoCompartir /> <b>Compartir</b> en Safari (si no lo ves, toca <b>···</b> primero).</li>
						<li>Elige <IconoAgregar /> <b>Agregar a inicio</b>.</li>
						<li>Abre WAPP desde su ícono y activa los avisos.</li>
					</ol>
				) : conBoton ? (
					<button className="am-btn" onClick={() => instalar().then((ok) => { if (ok) setNota("WAPP quedó instalada. Ábrela desde su ícono."); })}>Instalar WAPP</button>
				) : android ? (
					<p>En Chrome, toca el menú <b>⋮</b> y elige <b>Instalar app</b>.</p>
				) : (
					<p>Ábrela en tu celular: <b>wapp.washingtonacademy.com/app</b></p>
				)}
			</section>

			<section className="am-caja">
				<h2>Avisos</h2>
				{avisos === null ? <p className="am-tenue">Revisando…</p>
					: avisos === "activos" ? (
						<>
							<p className="am-ok">Avisos activos en este aparato.</p>
							<button className="am-btn am-btn-sec" disabled={ocupado} onClick={probar}>Mandar Aviso de Prueba</button>
						</>
					) : avisos === "inactivos" ? (
						<button className="am-btn" disabled={ocupado} onClick={activar}>{ocupado ? "Activando…" : "Activar Avisos"}</button>
					) : avisos === "instalar-primero" ? (
						<p>En iPhone los avisos llegan solo con WAPP instalada (iOS 16.4 o más nuevo).</p>
					) : avisos === "bloqueados" ? (
						<p className="am-malo">{ios ? "Los avisos están bloqueados. Actívalos en Ajustes › Notificaciones › WAPP."
							: "Los avisos están bloqueados. Permítelos en la configuración del sitio (el candado junto a la dirección) o de la app."}</p>
					) : (
						<p className="am-malo">{ios ? "Actualiza tu iPhone a iOS 16.4 o más nuevo para recibir avisos." : "Este navegador no recibe avisos. Usa Chrome."}</p>
					)}
				{nota ? <p className="am-nota">{nota}</p> : null}
				{error ? <p className="am-malo">{error}</p> : null}
			</section>
		</div>
	);
};

export default AppMovilView;
